import { GetCompanySearchResponse } from '@iwoca/lapi-client/edge';
import { getCookie } from 'utils/cookieHelper';
import { TDrawdownRepaymentQueryParams, URLS } from 'utils/tests/urls.config';

import { TGetOffersResponse } from './lapiOverrides.types';
import {
  TGetDocumentsResponse,
  TGetCompanyLegalFormResponse,
} from './lendingApi.types';

type TUpdateWithdrawalFee = {
  offerId: string;
  applicationId: string;
  introducerId: string | undefined;
  feeRate: number;
  setLoading: (isLoading: boolean) => void;
  onError: () => void;
};

export async function lendingAPIGetDocuments(
  state_key: string,
): Promise<TGetDocumentsResponse> {
  const response = await lendingApiFetchJson(
    URLS.getDocumentUpload({ stateKey: state_key }),
  );
  return response;
}

export const getLoginUrl = () => `/login/passwordless/email`;

async function redirectOnError() {
  try {
    const profileResponse = await lendingApiFetchJson(URLS.profile());

    if (profileResponse.data.user_type === 'staff') {
      return;
    }
    throw new Error('error fetching context');
  } catch {
    if (
      import.meta.env.MODE === 'production' ||
      import.meta.env.MODE === 'staging'
    ) {
      window.location.assign(getLoginUrl());
    }
  }
}

const getImpersonatingContextUrl = async (introducerId: string) => {
  if (introducerId.includes('-')) {
    // Remove existing impersonation first
    await URLS.introducerContext({ query: { __impersonate: 0 } });
    return URLS.introducerContextWithId({ introducerId });
  }

  return URLS.introducerContext({ query: { __impersonate: introducerId } });
};

export const getIntroducerContext = async (
  impersonatingIntroducerId?: string | null,
) => {
  if (impersonatingIntroducerId) {
    return getImpersonatingIntroducerContext(impersonatingIntroducerId);
  }

  const response = await lendingApiFetchJson(
    URLS.introducerContext({}),
    {},
    {},
    redirectOnError,
  );

  return response;
};

const getImpersonatingIntroducerContext = async (introducerId: string) => {
  const impersonatingContextUrl =
    await getImpersonatingContextUrl(introducerId);

  const response = await lendingApiFetch(impersonatingContextUrl);

  if (response.ok) {
    return response.json();
  }

  if (response.status === 403) {
    const profileResponse = await lendingApiFetchJson(URLS.profile());
    if (profileResponse.data.impersonator_type) {
      const unimpersonateResponse = await lendingApiFetch(
        '/my_account/loan/?__unimpersonate=0',
      );
      if (!unimpersonateResponse.ok && unimpersonateResponse.status !== 403) {
        throw Error(unimpersonateResponse.statusText);
      }

      const contextRetryResponse = await lendingApiFetchJson(
        impersonatingContextUrl,
      );

      return contextRetryResponse;
    }
  }

  throw Error(response.statusText);
};

export async function lendingAPIPutWithdrawalFee({
  introducerId,
  applicationId,
  offerId,
  feeRate,
  onError,
  setLoading,
}: TUpdateWithdrawalFee) {
  setLoading(true);
  const response = await lendingApiFetch(
    URLS.withdrawalFeeRate({ introducerId, applicationId, offerId }),
    {
      method: 'PUT',
      body: JSON.stringify({ data: { withdrawal_fee_rate: feeRate } }),
    },
  );

  if (!response.ok) {
    onError();
  }

  setLoading(false);

  return response;
}

export function fetchGetCompanySearch<T = GetCompanySearchResponse>({
  companyName,
}: {
  companyName: string;
}): Promise<T> {
  return lendingApiFetchJson(URLS.companySearch({ companyName }));
}

export function fetchGetCompanyLegalForm({
  crefoId,
}: {
  crefoId: string;
}): Promise<TGetCompanyLegalFormResponse> {
  return lendingApiFetchJson(
    URLS.getLegalForm({ query: { crefo_id: crefoId } }),
  );
}

export async function fetchSaveUploadedDocuments({
  stateKey,
  body,
}: {
  stateKey: string;
  body: FormData;
}) {
  const response = await lendingApiFetchWithoutContentType(
    URLS.getDocumentUpload({ stateKey }),
    { method: 'POST', body },
    { enctype: 'multipart/form-data' },
  );
  return await response.json();
}

export async function fetchGetIntroducerPortalApiOffers(
  introducer_id: string,
  application_id: string,
): Promise<TGetOffersResponse & { status: number }> {
  const response = await lendingApiFetchJson(
    URLS.getOffers({
      introducerId: introducer_id,
      applicationId: application_id,
    }),
  );
  return response;
}

export async function postIntroducerDocuments({
  introducerId,
  body,
}: {
  introducerId: string;
  body: FormData;
}) {
  const response = await lendingApiFetchWithoutContentType(
    URLS.postIntroducerDocuments({ introducerId }),
    { method: 'POST', body },
    { enctype: 'multipart/form-data' },
  );
  return await response.json();
}

export async function fetchGetEarlyRepaymentSchedulePDF({
  introducerId,
  applicationId,
  offerId,
  query,
}: {
  introducerId: string;
  applicationId: string;
  offerId: string;
  query: TDrawdownRepaymentQueryParams;
}) {
  const response = await lendingApiFetchWithoutContentType(
    URLS.getDrawdownRepaymentSchedule({
      introducerId,
      applicationId,
      offerId,
      query,
    }),
    { method: 'GET' },
    { Accept: 'application/pdf' },
  );

  return response;
}

export const lendingApiFetchJson = async (
  url: string,
  options = {},
  headers = {},
  errorHandler = () => {},
  customSuccessResponse: any = null,
) => {
  const response = await lendingApiFetch(url, options, headers);

  if (!response.ok) {
    if (errorHandler) {
      errorHandler();
    }

    throw Error(response.statusText);
  }

  return customSuccessResponse || response.json();
};

const API_AUTH_TOKEN = 'Token iwoca-9e4606290c6294a6b40e15c9bb75cd2daf0';

export const lendingApiFetch = (url: string, options = {}, headers = {}) =>
  // eslint-disable-next-line iwoca/prefer-lapi-client
  fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json;charset=UTF-8',
      authorization: API_AUTH_TOKEN,
      'x-csrftoken': getCookie('csrftoken') as string,
      ...headers,
    },
  });

const lendingApiFetchWithoutContentType = (
  url: string,
  options = {},
  headers = {},
) =>
  // eslint-disable-next-line iwoca/prefer-lapi-client
  fetch(url, {
    ...options,
    headers: {
      authorization: API_AUTH_TOKEN,
      'x-csrftoken': getCookie('csrftoken') as string,
      ...headers,
    },
  });
